/* eslint-disable */
import styles from './index.module.scss'
import Useful from '../../components/pages/useful'
import translate from '../../utils/translate.json'
import { t, calc5Ext, calcCommon } from '../../utils/calculatorTranslations'
import { PageContext } from '../../context/PageContextProvider'
import { useState, useContext, useEffect, useMemo } from 'react'
import { Calculator, Info, Attention } from '../../icons'
import PagesAPI from '../../api/pages'
import Loading from '../../components/pages/loading'
import StepLine from '../../components/core/stepLine'
import {
  STATUTORY_BASE_PARAMS,
  getPrecedingFourQuarters,
  checkSickLeaveEligibility,
  calculateSickLeaveBenefit2026,
} from './sickLeaveCalculator2026'

const EXCLUSION_REASONS = [
  'Əmək qabiliyyətinin müvəqqəti itirildiyi dövr (bülleten)',
  'Hamiləlik və doğuma görə məzuniyyət dövrü (dekret)',
  'Uşağa qulluq üçün qismən ödənişli sosial məzuniyyət',
  'Ödənişsiz (əməkhaqqı saxlanılmayan) məzuniyyət',
  'İstehsalatdan ayrılmaqla təhsil alınan dövr',
  'Hərbi toplanışlarda iştirak edilən dövr',
  'Müəssisənin təqsiri üzündən boşdayanma dövrü',
  'Digər qanuni əsasla işdən kənarlaşdırılma',
]

const Calculator5 = ({ className }) => {
  const { language } = useContext(PageContext)
  const [load, setLoad] = useState(false)
  const [info, setInfo] = useState([])

  // Basic Sick Leave Parameters
  const [caseType, setCaseType] = useState('ORDINARY_ILLNESS')
  const [employerFaultStatus, setEmployerFaultStatus] = useState('UNKNOWN')
  const [eventStartDate, setEventStartDate] = useState('2026-09-15')
  const [eventEndDate, setEventEndDate] = useState('2026-09-30')

  // Continuation parameters
  const [isContinuation, setIsContinuation] = useState(false)
  const [previouslyPaidEmployerDays, setPreviouslyPaidEmployerDays] = useState(0)

  // Seniority
  const [seniorityYears, setSeniorityYears] = useState(2)
  const [seniorityMonths, setSeniorityMonths] = useState(4)
  const [seniorityDays, setSeniorityDays] = useState(10)

  // Salary & Job
  const [monthlySalary, setMonthlySalary] = useState(1500)
  const [isAdditionalJob, setIsAdditionalJob] = useState(false)
  const [additionalJobMonthlySalary, setAdditionalJobMonthlySalary] = useState(0)

  // 4 Quarters Earnings Map: key -> earnings
  const [quarterlyEarnings, setQuarterlyEarnings] = useState({})

  // Excluded Days List: [{ id, reason, days }]
  const [excludedDaysList, setExcludedDaysList] = useState([
    { id: 1, reason: 'Ödənişsiz (əməkhaqqı saxlanılmayan) məzuniyyət', days: 0 },
  ])

  // Layer 2: Monthly Other Incomes { monthKey: { salary, vacation } }
  const [monthlyOtherIncomes, setMonthlyOtherIncomes] = useState({})

  useEffect(() => {
    (async () => {
      try {
        const data1 = await PagesAPI.getMagazinePageInfo()
        setInfo(data1?.data || [])
      } catch (e) {}
      setLoad(true)
    })()
  }, [])

  // Auto-detect 4 quarters based on eventStartDate
  const quarterInfo = useMemo(() => {
    try {
      return getPrecedingFourQuarters(eventStartDate)
    } catch (e) {
      return { quarters: [], totalBaseDays: 365 }
    }
  }, [eventStartDate])

  // Initialize quarterly earnings default when quarters change
  useEffect(() => {
    if (quarterInfo.quarters.length > 0) {
      setQuarterlyEarnings((prev) => {
        const next = { ...prev }
        quarterInfo.quarters.forEach((q) => {
          if (next[q.key] === undefined) {
            next[q.key] = 4500 // default 1500 * 3 = 4500
          }
        })
        return next
      })
    }
  }, [quarterInfo])

  // Total sick days (TGS)
  const totalSickDays = useMemo(() => {
    try {
      const s = new Date(eventStartDate)
      const e = new Date(eventEndDate)
      if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return 0
      const diff = Math.abs(e - s)
      return Math.round(diff / (1000 * 60 * 60 * 24)) + 1
    } catch (e) {
      return 0
    }
  }, [eventStartDate, eventEndDate])

  // Check Eligibility live
  const eligibility = useMemo(() => {
    return checkSickLeaveEligibility({
      eventStartDate,
      seniorityYears: Number(seniorityYears) || 0,
      seniorityMonths: Number(seniorityMonths) || 0,
      seniorityDays: Number(seniorityDays) || 0,
      caseType,
      employerFaultStatus,
    })
  }, [eventStartDate, seniorityYears, seniorityMonths, seniorityDays, caseType, employerFaultStatus])

  // Total earnings in 4 quarters
  const totalEarnings4Quarters = useMemo(() => {
    return quarterInfo.quarters.reduce((sum, q) => {
      return sum + (Number(quarterlyEarnings[q.key]) || 0)
    }, 0)
  }, [quarterInfo, quarterlyEarnings])

  // Total excluded days (ÇTG)
  const totalExcludedDays = useMemo(() => {
    return excludedDaysList.reduce((sum, item) => sum + (Number(item.days) || 0), 0)
  }, [excludedDaysList])

  // Base Dividing Days (BTG = ÜTG - ÇTG)
  const BTG = useMemo(() => {
    return Math.max(1, quarterInfo.totalBaseDays - totalExcludedDays)
  }, [quarterInfo, totalExcludedDays])

  // Daily rate preview
  const dailyRateOQMPreview = useMemo(() => {
    if (totalEarnings4Quarters > 0) {
      return (totalEarnings4Quarters / BTG).toFixed(4)
    }
    const startD = new Date(eventStartDate)
    const daysInMonth = new Date(startD.getFullYear(), startD.getMonth() + 1, 0).getDate() || 30
    return (STATUTORY_BASE_PARAMS.MINIMUM_WAGE_2026 / daysInMonth).toFixed(4)
  }, [totalEarnings4Quarters, BTG, eventStartDate])

  // Excluded days handlers
  const addExclusionRow = () => {
    setExcludedDaysList([
      ...excludedDaysList,
      { id: Date.now(), reason: EXCLUSION_REASONS[0], days: 0 },
    ])
  }

  const removeExclusionRow = (id) => {
    if (excludedDaysList.length <= 1) return
    setExcludedDaysList(excludedDaysList.filter((item) => item.id !== id))
  }

  const updateExclusionRow = (id, field, val) => {
    setExcludedDaysList(
      excludedDaysList.map((item) => (item.id === id ? { ...item, [field]: val } : item))
    )
  }

  // Intersected months for Layer 2 simulation
  const intersectedMonths = useMemo(() => {
    try {
      const s = new Date(eventStartDate)
      const e = new Date(eventEndDate)
      if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return []
      const cur = new Date(s)
      const list = []
      while (cur <= e) {
        const y = cur.getFullYear()
        const m = String(cur.getMonth() + 1).padStart(2, '0')
        const k = `${y}-${m}`
        if (!list.includes(k)) list.push(k)
        cur.setDate(cur.getDate() + 1)
      }
      return list
    } catch (e) {
      return []
    }
  }, [eventStartDate, eventEndDate])

  const steps = [
    {
      title: translate.calculation_type,
      text: translate.make_your_choice,
    },
    {
      title: translate.basic_information,
      text: translate.enter_info,
    },
    {
      title: translate.result,
      text: translate.ready,
    },
  ]

  const handleCalculate = () => {
    const inputPayload = {
      eventStartDate,
      eventEndDate,
      caseType,
      employerFaultStatus,
      seniorityYears: Number(seniorityYears) || 0,
      seniorityMonths: Number(seniorityMonths) || 0,
      seniorityDays: Number(seniorityDays) || 0,
      monthlySalary: Number(monthlySalary) || 0,
      isAdditionalJob,
      additionalJobMonthlySalary: Number(additionalJobMonthlySalary) || 0,
      quarterlyEarnings,
      excludedDaysList,
      isContinuation,
      previouslyPaidEmployerDays: Number(previouslyPaidEmployerDays) || 0,
      monthlyOtherIncomes,
    }

    const result = calculateSickLeaveBenefit2026(inputPayload)

    // Store in sessionStorage for Result5
    try {
      sessionStorage.setItem('sickleave_result_2026', JSON.stringify(result))
    } catch (e) {
      console.error(e)
    }

    // Direct / fallback navigation
    const gross = result.grossBenefit
    const emp = result.employerShareGross
    const dsmf = result.dsmfShareGross
    const tax = result.taxSimulation.totalEstimatedTax

    window.location.href = `/useful/calculator/5/result/${gross}/${emp}/${dsmf}/${tax}`
  }

  return load ? (
    <section className={`${styles.container} ${className || ''}`}>
      <Useful search={false} />
      <div className={styles.stepline}>
        <StepLine active={1} data={steps} />
      </div>

      <h1 className={styles.pageTitle}>
        <Calculator />
        {translate.sick_leave?.[language] ||
          'Əmək qabiliyyətinin müvəqqəti itirilməsinə görə (Xəstəlik vərəqəsi) müavinət kalkulyatoru (2026)'}
      </h1>

      <div className={styles.formCard}>
        {/* BÖLMƏ 1: Hadisə və Xəstəlik Məlumatları */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionNum}>1</div>
          <h3>{language === 'TR' ? 'Vaka ve Rapor Bilgileri' : language === 'ENG' ? 'Incident & Sick Leave Details' : language === 'RU' ? 'Данные о случае и больничном листе' : 'Hadisə və Xəstəlik Vərəqəsi Məlumatları'}</h3>
        </div>

        <div className={styles.grid2}>
          <div className={styles.inputGroup}>
            <label>{language === 'TR' ? 'Vakanın hukuki türü' : language === 'ENG' ? 'Legal type of incident' : language === 'RU' ? 'Юридический тип случая' : 'Hadisənin hüquqi növü'}</label>
            <select value={caseType} onChange={(e) => setCaseType(e.target.value)}>
              <option value="ORDINARY_ILLNESS">{t(calc5Ext.generalIllness, language)}</option>
              <option value="OCCUPATIONAL_ACCIDENT_OR_DISEASE">
                İstehsalat qəzası və ya peşə xəstəliyi (ƏM m.74 / Qayda 2.17)
              </option>
            </select>
            <span className={styles.helperText}>
              İstehsalat zədəsi zamanı 2x maaş məhdudiyyəti tətbiq olunmur (yalnız 25x pensiya limiti qalır).
            </span>
          </div>

          {caseType === 'OCCUPATIONAL_ACCIDENT_OR_DISEASE' && (
            <div className={styles.inputGroup}>
              <label>Sığortaedənin (işəgötürənin) təqsir statusu</label>
              <select
                value={employerFaultStatus}
                onChange={(e) => setEmployerFaultStatus(e.target.value)}
              >
                <option value="UNKNOWN">Məlum deyil / Araşdırma davam edir (İZ aktı yoxdur)</option>
                <option value="CONFIRMED">
                  Təsdiqlənib (İZ aktı ilə təqsir sübut olunub — Qayda 2.17 üzrə 100% işəgötürən ödəyir)
                </option>
                <option value="NOT_CONFIRMED">Təsdiqlənməyib (İşəgötürənin təqsiri müəyyən edilməyib)</option>
              </select>
              <span className={styles.helperText}>
                Qayda 2.17: Təqsir yalnız rəsmi İZ aktı ilə təsdiq edildikdə bütün dövr işəgötürənin vəsaiti hesabına maliyyələşir.
              </span>
            </div>
          )}
        </div>

        {caseType === 'OCCUPATIONAL_ACCIDENT_OR_DISEASE' && (
          <div className={styles.alertWarning}>
            <Attention />
            <div>
              <strong>Xüsusi İstehsalat Rejimi:</strong> İstehsalat qəzası və ya peşə xəstəliyində 2 misli vəzifə maaşı tavanı tətbiq edilmir, lakin 25 misli minimum pensiya tavanı (8 000 AZN) qorunur. Sığorta stajı 6 aydan az olduqda hüquqi status üçün xüsusi İZ araşdırması tələb olunur.
            </div>
          </div>
        )}

        <div className={styles.grid3}>
          <div className={styles.inputGroup}>
            <label>{t(calc5Ext.bulletinStart, language)}</label>
            <input
              type="date"
              value={eventStartDate}
              onChange={(e) => setEventStartDate(e.target.value)}
            />
            <span className={styles.helperText}>{t(calc5Ext.bulletinStartHint, language)}</span>
          </div>

          <div className={styles.inputGroup}>
            <label>{t(calc5Ext.bulletinEnd, language)}</label>
            <input
              type="date"
              value={eventEndDate}
              onChange={(e) => setEventEndDate(e.target.value)}
            />
            <span className={styles.helperText}>{t(calc5Ext.bulletinEndHint, language)}</span>
          </div>

          <div className={styles.inputGroup}>
            <label>Cəmi xəstəlik təqvim günləri (TGS)</label>
            <input type="text" disabled value={`${totalSickDays} gün`} />
            <span className={styles.helperText}>
              İlk 14 günədək işəgötürən, 15-ci gündən DSMF tərəfindən ödənilir
            </span>
          </div>
        </div>

        {/* Continuation Toggle */}
        <div className={styles.tableCard}>
          <label className={styles.checkboxGroup}>
            <input
              type="checkbox"
              checked={isContinuation}
              onChange={(e) => setIsContinuation(e.target.checked)}
            />
            <span>Bu bülleten əvvəlki xəstəlik vərəqəsinin fasiləsiz davamıdır (Qayda 2.5)</span>
          </label>

          {isContinuation && (
            <div className={styles.inputGroup} style={{ maxWidth: '400px', marginTop: '10px' }}>
              <label>{t(calc5Ext.continuationDaysLabel, language)}</label>
              <input
                type="number"
                min="0"
                max="14"
                value={previouslyPaidEmployerDays}
                onChange={(e) => setPreviouslyPaidEmployerDays(e.target.value)}
              />
              <span className={styles.helperText}>
                Fasiləsiz xəstəlik davam edirsə, işəgötürənin 14 günlük limiti yenidən başlamır (əvvəlki günlər çıxılır).
              </span>
            </div>
          )}
        </div>

        {/* BÖLMƏ 2: Sosial Sığorta Stajı və Hüququn Təyini */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionNum}>2</div>
          <h3>{language === 'TR' ? 'Sosyal Güvenlik Hizmet Süresi ve Yasal Hak (Kural 1.5)' : language === 'ENG' ? 'Social Insurance Seniority & Statutory Right (Rule 1.5)' : language === 'RU' ? 'Страховой стаж и законное право (Правило 1.5)' : 'Sosial Sığorta Stajı və Qanuni Hüquq (Qayda 1.5)'}</h3>
        </div>

        <div className={styles.grid3}>
          <div className={styles.inputGroup}>
            <label>Staj (İl)</label>
            <input
              type="number"
              min="0"
              value={seniorityYears}
              onChange={(e) => setSeniorityYears(e.target.value)}
            />
          </div>
          <div className={styles.inputGroup}>
            <label>Staj (Ay)</label>
            <input
              type="number"
              min="0"
              max="11"
              value={seniorityMonths}
              onChange={(e) => setSeniorityMonths(e.target.value)}
            />
          </div>
          <div className={styles.inputGroup}>
            <label>Staj (Gün)</label>
            <input
              type="number"
              min="0"
              max="30"
              value={seniorityDays}
              onChange={(e) => setSeniorityDays(e.target.value)}
            />
          </div>
        </div>

        {/* Dynamic Eligibility Live Feedback */}
        {eligibility.eligible ? (
          <div className={styles.alertSuccess}>
            <Info />
            <div>
              <strong>🟢 {eligibility.rateLabel}:</strong> {eligibility.statusMessage}
            </div>
          </div>
        ) : (
          <div className={styles.alertError}>
            <Attention />
            <div>
              <strong>🔴 {eligibility.rateLabel}:</strong> {eligibility.statusMessage}
            </div>
          </div>
        )}

        {/* BÖLMƏ 3: Əməkhaqqı və Tavan Göstəriciləri */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionNum}>3</div>
          <h3>Cari Əməkhaqqı və Qanuni Tavan Limitləri</h3>
        </div>

        <div className={styles.grid2}>
          <div className={styles.inputGroup}>
            <label>Əsas iş yeri üzrə aylıq vəzifə maaşı (AZN)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={monthlySalary}
              onChange={(e) => setMonthlySalary(e.target.value)}
            />
            <span className={styles.helperText}>
              Bənd 2.10.1: Aylıq müavinət məbləği vəzifə maaşının 2 mislindən artıq ola bilməz.
            </span>
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.checkboxGroup} style={{ marginTop: '32px' }}>
              <input
                type="checkbox"
                checked={isAdditionalJob}
                onChange={(e) => setIsAdditionalJob(e.target.checked)}
              />
              <span>Əlavə (ştatdankənar/ikinci) iş yeri üzrə gəlir var</span>
            </label>
          </div>
        </div>

        {isAdditionalJob && (
          <div className={styles.inputGroup} style={{ maxWidth: '400px' }}>
            <label>Əlavə iş yeri üzrə aylıq vəzifə maaşı (AZN)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={additionalJobMonthlySalary}
              onChange={(e) => setAdditionalJobMonthlySalary(e.target.value)}
            />
            <span className={styles.helperText}>
              2 misli maaş tavanı hesablanarkən əsas və əlavə iş yerinin maaşları cəmlənir.
            </span>
          </div>
        )}

        <div className={styles.grid3}>
          <div className={styles.infoBadge}>
            Minimum Pensiya: {STATUTORY_BASE_PARAMS.MINIMUM_PENSION_2026} AZN
          </div>
          <div className={styles.infoBadge}>
            Maksimum Tavan (25x): {STATUTORY_BASE_PARAMS.MINIMUM_PENSION_2026 * STATUTORY_BASE_PARAMS.PENSION_CAP_MULTIPLIER} AZN
          </div>
          <div className={styles.infoBadge}>
            Minimum Əməkhaqqı (Bənd 2.9): {STATUTORY_BASE_PARAMS.MINIMUM_WAGE_2026} AZN
          </div>
        </div>

        {/* BÖLMƏ 4: Hadisədən Əvvəlki Ardıcıl 4 Rüblük Qazanc */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionNum}>4</div>
          <h3>Hesablama Bazasının Qazancı (Hadisədən Əvvəlki Ardıcıl 4 Rüb — Qayda 2.1)</h3>
        </div>

        <div className={styles.alertInfo}>
          <Info />
          <div>
            Hadisənin başlanma tarixinə ({eventStartDate}) əsasən sistem avtomatik olaraq əvvəlki ardıcıl 4 rübü
            müəyyən etmişdir: <strong>{quarterInfo.basePeriodStart}</strong> — <strong>{quarterInfo.basePeriodEnd}</strong> (Cəmi {quarterInfo.totalBaseDays} təqvim günü). Hər rüb üzrə yalnız MDSS hesablanmış leqal gəlirləri daxil edin.
          </div>
        </div>

        <div className={styles.quartersGrid}>
          {quarterInfo.quarters.map((q) => (
            <div key={q.key} className={styles.quarterCard}>
              <div className={styles.quarterHeader}>
                <span className={styles.label}>{q.label}</span>
                <span className={styles.days}>{q.calendarDays} təqvim günü</span>
              </div>
              <div className={styles.inputGroup}>
                <label>Rüblük Qazanc (AZN)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={quarterlyEarnings[q.key] !== undefined ? quarterlyEarnings[q.key] : ''}
                  onChange={(e) =>
                    setQuarterlyEarnings({
                      ...quarterlyEarnings,
                      [q.key]: e.target.value,
                    })
                  }
                  placeholder="0.00"
                />
              </div>
            </div>
          ))}
        </div>

        {totalEarnings4Quarters === 0 && (
          <div className={styles.alertWarning}>
            <Attention />
            <div>
              <strong>Bənd 2.9 Qaydası:</strong> Əvvəlki 4 rübdə sosial sığorta haqqı hesablanan qazanc olmadıqda,
              bir günlük orta qazanc ölkə üzrə müəyyən edilmiş minimum aylıq əməkhaqqının ({STATUTORY_BASE_PARAMS.MINIMUM_WAGE_2026} AZN)
              hadisənin baş verdiyi ayın təqvim günlərinə bölünməsi ilə təyin edilir.
            </div>
          </div>
        )}

        {/* BÖLMƏ 5: Çıxılan Təqvim Günləri (ÇTG — Qayda 2.2) */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionNum}>5</div>
          <h3>Qanunvericiliklə Nəzərdə Tutulan Çıxılan Təqvim Günləri (ÇTG — Qayda 2.2)</h3>
        </div>

        <div className={styles.tableCard}>
          <div style={{ fontSize: '13px', color: '#475569', marginBottom: '8px' }}>
            Qayda 2.2-yə əsasən, 4 rüblük dövrə düşən bülleten, dekret, uşağa qulluq məzuniyyəti və ödənişsiz məzuniyyət günləri
            ümumi təqvim günlərindən (ÜTG) çıxılır və bölən təqvim günlərini (BTG) təşkil edir.
          </div>

          {excludedDaysList.map((row) => (
            <div key={row.id} className={styles.grid3} style={{ alignItems: 'flex-end' }}>
              <div className={styles.inputGroup} style={{ flex: 2 }}>
                <label>Çıxılma səbəbi</label>
                <select
                  value={row.reason}
                  onChange={(e) => updateExclusionRow(row.id, 'reason', e.target.value)}
                >
                  {EXCLUSION_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.inputGroup} style={{ flex: 1 }}>
                <label>Çıxılan günlər</label>
                <input
                  type="number"
                  min="0"
                  value={row.days}
                  onChange={(e) => updateExclusionRow(row.id, 'days', e.target.value)}
                />
              </div>

              <div>
                <button
                  type="button"
                  className={styles.btnDanger}
                  onClick={() => removeExclusionRow(row.id)}
                  disabled={excludedDaysList.length <= 1}
                >
                  Sil
                </button>
              </div>
            </div>
          ))}

          <div className={styles.actionRow}>
            <button type="button" className={styles.btnOutline} onClick={addExclusionRow}>
              + Yeni çıxılan dövr əlavə et
            </button>
          </div>

          <div className={styles.summaryBox}>
            <span>
              ÜTG: <strong>{quarterInfo.totalBaseDays} gün</strong> | ÇTG: <strong>{totalExcludedDays} gün</strong> | BTG: <strong>{BTG} gün</strong>
            </span>
            <span>
              Təxmini OQM: <strong>{dailyRateOQMPreview} AZN / gün</strong>
            </span>
          </div>
        </div>

        {/* BÖLMƏ 6: Layer 2 Aylıq Mühasibat və Vergi Rekonsilyasiyası */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionNum}>6</div>
          <h3>Layer 2: Aylıq Əməkhaqqı və Vergi Simulyasiyası (Könüllü / Təkmil)</h3>
        </div>

        <div className={styles.alertInfo}>
          <Info />
          <div>
            <strong>Vergi Məcəlləsi (VM 102.1.4 və 102.1.6):</strong> Xəstəlik vərəqəsi müavinəti (həm işəgötürən, həm DSMF hissəsi)
            gəlir vergisinə cəlb olunur. Müavinətdən MDSS, İTS və İŞS tutulmur (0%). Xəstələndiyiniz aylarda işlənmiş günlərə görə
            faktiki maaş və məzuniyyət haqqı məbləğlərini daxil edərək aylıq vahid gəlir vergisi və xalis ödənişi simulyasiya edə bilərsiniz.
          </div>
        </div>

        <div className={styles.grid2}>
          {intersectedMonths.map((mKey) => {
            const data = monthlyOtherIncomes[mKey] || { salary: 0, vacation: 0 }
            return (
              <div key={mKey} className={styles.quarterCard}>
                <div className={styles.quarterHeader}>
                  <span className={styles.label}>{mKey} ayı üzrə digər gəlirlər</span>
                  <span className={styles.days}>Muzdlu Vergi Bazası</span>
                </div>
                <div className={styles.inputGroup}>
                  <label>Bu ayda işlənmiş günlərə görə maaş (AZN)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={data.salary || ''}
                    onChange={(e) =>
                      setMonthlyOtherIncomes({
                        ...monthlyOtherIncomes,
                        [mKey]: { ...data, salary: e.target.value },
                      })
                    }
                    placeholder="0.00"
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label>Bu aya düşən məzuniyyət haqqı (AZN)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={data.vacation || ''}
                    onChange={(e) =>
                      setMonthlyOtherIncomes({
                        ...monthlyOtherIncomes,
                        [mKey]: { ...data, vacation: e.target.value },
                      })
                    }
                    placeholder="0.00"
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <button type="button" className={styles.calculateBtn} onClick={handleCalculate}>
          <Calculator />
          Müavinəti Hesabla
        </button>
      </div>
    </section>
  ) : (
    <Loading />
  )
}

export default Calculator5
